import { useState } from "react";
import { formatDateOnly } from "@/lib/date-only";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { apiGet, apiPost, apiPatch } from "@/lib/api";
import { toast } from "sonner";
import {
  ChevronLeft,
  Send,
  CheckCircle,
  MessageCircle,
  Loader2,
} from "lucide-react";
import { useUnsavedChanges } from "@/hooks/use-unsaved-changes";
import { useGoBack } from "@/hooks/use-go-back";

export const Route = createFileRoute(
  "/_authenticated/corrective-actions/$actionId"
)({
  component: CorrectiveActionDetailPage,
});

function CorrectiveActionDetailPage() {
  const { actionId } = Route.useParams();
  const navigate = useNavigate();
  const goBack = useGoBack({ to: "/corrective-actions" });
  const queryClient = useQueryClient();
  const isNew = actionId === "new";

  // Form state for new action
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState("medium");
  const [dueDate, setDueDate] = useState("");

  // Comment state
  const [newComment, setNewComment] = useState("");

  // Closure state
  const [closureNotes, setClosureNotes] = useState("");

  const isDirty = isNew ? !!(title || description) : !!(closureNotes || newComment);
  useUnsavedChanges(isDirty);

  const { data, isLoading } = useQuery({
    queryKey: ["corrective-action", actionId],
    queryFn: () =>
      apiGet<{ data: any }>(`/api/corrective-actions/${actionId}`),
    enabled: !isNew,
  });

  const action = data?.data;

  const createMutation = useMutation({
    mutationFn: (body: any) =>
      apiPost<{ data: any }>("/api/corrective-actions", body),
    onSuccess: (res) => {
      toast.success("Corrective action created");
      queryClient.invalidateQueries({ queryKey: ["corrective-actions"] });
      navigate({
        to: "/corrective-actions/$actionId",
        params: { actionId: res.data.id },
      });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const closeMutation = useMutation({
    mutationFn: () =>
      apiPost(`/api/corrective-actions/${actionId}/close`, { closureNotes }),
    onSuccess: () => {
      toast.success("Action closed");
      queryClient.invalidateQueries({ queryKey: ["corrective-action", actionId] });
      queryClient.invalidateQueries({ queryKey: ["corrective-actions"] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const commentMutation = useMutation({
    mutationFn: () =>
      apiPost(`/api/corrective-actions/${actionId}/comments`, {
        comment: newComment,
      }),
    onSuccess: () => {
      setNewComment("");
      queryClient.invalidateQueries({ queryKey: ["corrective-action", actionId] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const statusMutation = useMutation({
    mutationFn: (status: string) =>
      apiPatch(`/api/corrective-actions/${actionId}`, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["corrective-action", actionId] });
      queryClient.invalidateQueries({ queryKey: ["corrective-actions"] });
    },
  });

  if (isNew) {
    return (
      <div className="space-y-4 max-w-lg mx-auto">
        <div className="flex items-center gap-2 pt-2">
          <Button variant="ghost" size="sm" className="p-1" onClick={goBack} aria-label="Back">
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-2xl font-bold text-text-primary">New Action</h1>
        </div>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Title *</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="What needs to be done?" maxLength={200} className="h-12" />
          </div>
          <div className="space-y-2">
            <Label>Description</Label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Details..." rows={3} maxLength={2000} className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-base" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Priority</Label>
              <select value={priority} onChange={(e) => setPriority(e.target.value)} className="flex h-12 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label>Due Date</Label>
              <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="h-12" />
            </div>
          </div>
          <Button
            className="w-full h-12 bg-paris-red hover:bg-paris-red-dark"
            disabled={!title.trim() || createMutation.isPending}
            onClick={() => createMutation.mutate({ title: title.trim(), description: description.trim(), priority, dueDate: dueDate || undefined })}
          >
            {createMutation.isPending ? <Loader2 className="h-5 w-5 animate-spin" /> : "Create Action"}
          </Button>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-4 max-w-lg mx-auto pt-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (!action) {
    return (
      <div className="text-center pt-12">
        <p className="text-muted-foreground">Action not found</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 max-w-lg mx-auto pb-20">
      <div className="flex items-center gap-2 pt-2">
        <Button variant="ghost" size="sm" className="p-1" onClick={goBack} aria-label="Back">
          <ChevronLeft className="h-5 w-5" />
        </Button>
        <h1 className="text-xl font-bold text-text-primary truncate">
          {action.title}
        </h1>
      </div>

      <Card>
        <CardContent className="py-3 px-4 space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge className={`text-xs ${action.priority === "critical" ? "bg-red-100 text-red-800" : action.priority === "high" ? "bg-orange-100 text-orange-800" : action.priority === "medium" ? "bg-yellow-100 text-yellow-800" : "bg-blue-100 text-blue-800"}`}>
              {action.priority}
            </Badge>
            <Badge variant="outline">{action.status.replace("_", " ")}</Badge>
            {action.sourceType && <Badge variant="secondary">{action.sourceType}</Badge>}
          </div>
          {action.description && <p className="text-sm text-muted-foreground">{action.description}</p>}
          {action.dueDate && <p className="text-xs text-muted-foreground">Due: {formatDateOnly(action.dueDate)}</p>}
          {action.closureNotes && (
            <div className="border-t pt-2 mt-2">
              <p className="text-xs font-medium text-green-700">Closure Notes</p>
              <p className="text-sm text-muted-foreground">{action.closureNotes}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Status actions */}
      {action.status !== "closed" && (
        <div className="flex gap-2">
          {action.status === "open" && (
            <Button variant="outline" className="flex-1" onClick={() => statusMutation.mutate("in_progress")}>
              Start Working
            </Button>
          )}
          <Button variant="outline" className="flex-1 text-green-700 border-green-300" onClick={() => {
            if (!closureNotes.trim()) {
              toast.error("Please add closure notes");
              return;
            }
            closeMutation.mutate();
          }}>
            <CheckCircle className="h-4 w-4 mr-1" /> Close
          </Button>
        </div>
      )}

      {action.status !== "closed" && (
        <div className="space-y-2">
          <Label>Closure Notes</Label>
          <textarea value={closureNotes} onChange={(e) => setClosureNotes(e.target.value)} placeholder="Describe how this was resolved..." rows={2} maxLength={2000} className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
        </div>
      )}

      {/* Comments */}
      <div className="space-y-3">
        <h3 className="text-sm font-semibold flex items-center gap-1">
          <MessageCircle className="h-4 w-4" /> Comments
        </h3>
        {(action.comments || []).map((c: any) => (
          <Card key={c.id}>
            <CardContent className="py-2 px-3">
              <p className="text-xs font-medium">{c.userName}</p>
              <p className="text-sm">{c.comment}</p>
              <p className="text-xs text-muted-foreground mt-1">
                {new Date(c.createdAt).toLocaleString()}
              </p>
            </CardContent>
          </Card>
        ))}
        <div className="flex gap-2">
          <Input value={newComment} onChange={(e) => setNewComment(e.target.value)} placeholder="Add a comment..." className="flex-1" onKeyDown={(e) => { if (e.key === "Enter" && newComment) commentMutation.mutate(); }} />
          <Button size="icon" disabled={!newComment || commentMutation.isPending} onClick={() => commentMutation.mutate()}>
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
